/**
 * ShopSales Presenter
 * --
 * 작성자 - Owen
 * 작성일 - 2025 07 11 금요일
 * 마지막 수정일 - 2025 07 11 금요일
 */
import { Button, Flex, Popover, Spin } from 'antd';
import { Col, Content, Row, Text, Title } from 'components';
import React from 'react';
import ShopList from './components/ShopList';
import StatisticsFilter from '../SaleDashboard/components/StatisticsFilter';
import ShopSales from './components/ShopSales';
import ShopStatistics from './components/ShopStatistics';
import { DownOutlined } from '@ant-design/icons';
import ShopSalesColumns from './components/ShopSalesColumns';
import 'dayjs/locale/ko';
import getTimezoneSalesData from './utils/getTimezoneSales';
import getProductSalesData from './utils/getProductSales';
import getCategorySalesData from './utils/getCategorySales';
import getPaymentSalesData from './utils/getPaymentSales';
import getDailySalesData from './utils/getDailySales';

const ShopSalesPresenter = (props) => {
  /**
   * SECTION - Init
   */
  const shopPath = ['category', 'products'];
  /**!SECTION - Init */

  /**
   * SECTION - Route
   */

  /**!SECTION - Route */

  /**
   * SECTION - Props
   */
  const {
    isLoading = false,
    auth = 'SHOP',
    params = 'daily',
    shopInformation = [],
    selectedShop = null,
    delivery = [],
    subscription = [],
    rental = [],
    orders = [],
    onSelect = () => {},
    onSelectPeriod = () => {},
    period = [],

    summaryColumns,
    setSummaryColumns,
    setTableColumns,
    onDownload,

    disabledColumns,
    onCheckbox,
  } = props;
  /**!SECTION - Props */

  /**
   * SECTION - Route
   */

  /**!SECTION - Route */

  /**
   * SECTION - Variable
   */
  const styles = {
    title: {
      fontSize: 24,
      fontWeight: 700,
    },
  };
  /**!SECTION - Variable */

  /**
   * SECTION - Query Data
   */

  /**!SECTION - Query Data */

  /**
   * SECTION - Mutate
   */

  /**!SECTION - Mutate */

  /**
   * SECTION - State
   */
  const [isOpened, setIsOpened] = React.useState(false);
  /**!SECTION - State */

  /**
   * SECTION - Function
   */
  /**
   * 타이틀 가져오기
   * --
   * @param {string} t
   * @returns
   */
  const getTitle = (t) => {
    switch (t) {
      case 'daily':
        return '일자별';
      case 'products':
        return '상품별';
      case 'options':
        return '옵션별';
      case 'timezone':
        return '시간대별';
      case 'payments':
        return '결제수단별';
      case 'category':
        return '카테고리별';
      default:
        return '';
    }
  };
  /**!SECTION - Function */

  /**
   * SECTION - Hooks
   */
  // 테이블 데이터 정제
  const TableData = React.useMemo(() => {
    switch (params) {
      case 'daily': {
        return getDailySalesData(
          orders,
          delivery,
          subscription,
          rental,
          period,
        );
      }
      case 'products': {
        return getProductSalesData(orders, delivery, subscription, rental);
      }
      case 'timezone': {
        return getTimezoneSalesData(orders, delivery, subscription, rental);
      }
      case 'payments': {
        return getPaymentSalesData(orders, delivery, subscription, rental);
      }
      case 'category': {
        return getCategorySalesData(orders, delivery, subscription, rental);
      }
      default: {
        return [];
      }
    }
  }, [params, orders, delivery, subscription, rental, period]);
  /**!SECTION - Hooks */

  /**
   * SECTION - Render
   */
  return (
    <React.Fragment>
      <Spin spinning={isLoading}>
        <Content maxWidth={'100%'} padding={20} backgroundColor="none">
          <Row>
            <Col x={24}>
              <Flex gap={8} align="center" style={{ marginBottom: 20 }}>
                <Title style={{ ...styles.title }}>{getTitle(params)}</Title>
              </Flex>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col x={auth === 'SHOP' ? 0 : 4}>
              <Content
                maxWidth={'100%'}
                padding={0}
                style={{
                  height: '80vh',
                  overflowY: 'scroll',
                  borderRadius: 7,
                }}
              >
                <ShopList
                  data={shopInformation}
                  selected={selectedShop}
                  onSelect={onSelect}
                />
              </Content>
            </Col>

            <Col x={auth === 'SHOP' ? 24 : 20}>
              <Row gutter={[16, 16]}>
                <Col x={24}>
                  <StatisticsFilter onReset={onSelectPeriod} />
                </Col>

                <Col x={24}>
                  <Flex justify="space-between" align="center">
                    <Flex align="center" gap={8}>
                      <Title>{getTitle(params)} 리포트</Title>
                      <Button
                        onClick={() => onDownload(getTitle(params), TableData)}
                      >
                        엑셀 다운로드
                      </Button>
                    </Flex>

                    <Popover
                      trigger="click"
                      open={isOpened}
                      onOpenChange={(open) => setIsOpened(open)}
                      placement="bottomRight"
                      title={'테이블 컬럼 관리'}
                      content={
                        <ShopSalesColumns
                          columns={summaryColumns}
                          checked={disabledColumns}
                          onChange={onCheckbox}
                        />
                      }
                    >
                      <Button
                        type="link"
                        icon={<DownOutlined />}
                        iconPosition="end"
                        onClick={() => setIsOpened(!isOpened)}
                      >
                        테이블 컬럼 관리
                      </Button>
                    </Popover>
                  </Flex>
                </Col>

                <Col x={24}>
                  <ShopStatistics
                    type={params}
                    setSummaryColumns={setSummaryColumns}
                    orders={orders}
                    delivery={delivery}
                    subscription={subscription}
                    rental={rental}
                    disabled={disabledColumns}
                  />
                </Col>

                <Col x={24}>
                  {shopPath.includes(params) ? (
                    <Text size={14} color={'#FF4242'}>
                      {getTitle(params)} 리포트에서는 Total, 순매출에 배송비가
                      포함되지 않습니다.
                    </Text>
                  ) : null}
                  <ShopSales
                    type={params}
                    data={TableData}
                    period={period}
                    columns={summaryColumns}
                    setTableColumns={setTableColumns}
                    disabled={disabledColumns}
                    shopId={selectedShop}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Content>
      </Spin>
    </React.Fragment>
  );
  /**!SECTION - Render */
};

export default ShopSalesPresenter;
